// apps/web/src/lib/vendas/fechamento.ts — Fase 12, Plano 14 (FECH-01, FECH-02, D-20..D-23, R12-09).
// Regras puras da aba Fechamento do PV: validação da quantidade por unidade, resumo da seleção agrupado por unidade
// (nunca soma unidades diferentes), cópia do diálogo de confirmação e apoio ao estado do faturamento. Nenhum valor
// fiscal ou de quantidade é calculado aqui: a API (simular/fechar) é a fonte de verdade.
import type {
  CodigoPendenciaRascunho,
  EstadoFaturamentoOmie,
  EstadoRascunhoFiscal,
  FonteConsumoResumida,
  PendenciaRascunhoFiscal,
  TipoLinhaRascunho,
} from '@pieltec/contracts';
import { apresentacaoFonte, type IconeFonteConsumo } from '../estoque/consumo-tinta';
import { mensagemNumeroDigitado, paraTexto } from './numero';
import { CASAS_MAXIMAS_QUANTIDADE, formatarQuantidade, unidadeInteira, unidadePorExtenso } from './formatacao';

/** kg, m² e demais unidades aceitam até 4 casas decimais (contrato do fechamento; mesma fonte da formatação). */
export const CASAS_MAXIMAS_FECHAMENTO = CASAS_MAXIMAS_QUANTIDADE;
const ESCALA = 10 ** CASAS_MAXIMAS_FECHAMENTO;
const TOLERANCIA = 1e-6;

const ESTADOS_FATURAMENTO: readonly EstadoFaturamentoOmie[] = ['aguardando_fila', 'emitindo_nfe', 'lancando_titulo', 'concluido', 'acao_necessaria'];

/** Quantidade com até 4 casas (regra única de `formatarQuantidade`). */
export function formatarQuantidadeFechamento(valor: number, unidade: string): string {
  return formatarQuantidade(valor, unidade);
}

export { unidadeInteira, unidadePorExtenso };

function inteiroOuDecimalValido(quantidade: number, unidade: string): boolean {
  if (unidadeInteira(unidade)) return Number.isInteger(quantidade);
  return Math.abs(quantidade * ESCALA - Math.round(quantidade * ESCALA)) < TOLERANCIA;
}

/**
 * Erro inline da "Quantidade a fechar" (0 < q <= disponível; inteira para peça/un; até 4 casas para kg e m²),
 * ou null quando vale. O servidor revalida sob lock — esta checagem só evita a ida e volta.
 */
export function validarQuantidadeFechamento(quantidade: number, disponivel: number, unidade: string, texto?: string): string | null {
  // Número ambíguo digitado ("1.500"): explica em vez do texto genérico (o valor não foi adivinhado).
  if (!Number.isFinite(quantidade) && texto !== undefined) {
    const explicacao = mensagemNumeroDigitado(texto, '');
    if (explicacao) return explicacao;
  }
  const invalida =
    !Number.isFinite(quantidade) || quantidade <= 0 || quantidade > disponivel + TOLERANCIA || !inteiroOuDecimalValido(quantidade, unidade);
  if (!invalida) return null;
  return `Informe uma quantidade maior que zero e no máximo ${formatarQuantidadeFechamento(disponivel, unidade)} ${unidadePorExtenso(disponivel, unidade)}.`;
}

/**
 * Resumo fixo da barra: '{N} lote(s) selecionado(s) · {Q1} peças · {Q2} kg'. Agrupa por unidade e nunca soma unidades
 * diferentes; sem seleção mostra só a contagem.
 */
export function resumoSelecao(selecionados: ReadonlyArray<{ readonly quantidade: number; readonly unidade: string }>): string {
  const cabecalho = `${selecionados.length} lote(s) selecionado(s)`;
  const somas = new Map<string, number>();
  for (const { quantidade, unidade } of selecionados) {
    somas.set(unidade, (somas.get(unidade) ?? 0) + quantidade);
  }
  if (somas.size === 0) return cabecalho;
  const partes = [...somas.entries()].map(([unidade, total]) => {
    const arredondado = Math.round(total * ESCALA) / ESCALA;
    return `${formatarQuantidadeFechamento(arredondado, unidade)} ${unidadePorExtenso(arredondado, unidade)}`;
  });
  return [cabecalho, ...partes].join(' · ');
}

/** Totais agrupados por unidade sem o cabeçalho de lotes ('10 peças · 0,75 kg'); '—' sem seleção. */
export function totaisSelecaoPorUnidade(selecionados: ReadonlyArray<{ readonly quantidade: number; readonly unidade: string }>): string {
  const resumo = resumoSelecao(selecionados);
  const partes = resumo.split(' · ').slice(1);
  return partes.length > 0 ? partes.join(' · ') : '—';
}

/** Corpo do diálogo 'Confirmar fechamento' (sem e com pendência — D-22). `pendencias` são textos legíveis. */
export function corpoConfirmacao({ lotes, pendencias }: { readonly lotes: number; readonly pendencias: readonly string[] }): string {
  const base = `Será gerado 1 Retorno com ${lotes} lote(s) e um rascunho fiscal de 3 linhas por item para revisão.`;
  if (pendencias.length === 0) return base;
  return `${base} O rascunho fiscal ficará pendente e não será enviado à Omie até resolver: ${pendencias.join(' · ')}.`;
}

/** Chave do mapa ESTADO_FISCAL_CONFIG a partir do estado do faturamento devolvido pela API (maiúsculo no banco). */
export function chaveEstadoFiscal(estado: string | null | undefined): EstadoFaturamentoOmie | null {
  if (!estado) return null;
  const chave = estado.toLowerCase();
  return (ESTADOS_FATURAMENTO as readonly string[]).includes(chave) ? (chave as EstadoFaturamentoOmie) : null;
}

// ---------------------------------------------------------------------------
// Rascunho fiscal de 3 linhas (FECH-02, D-16..D-18, R12-06, T-12-79)
// ---------------------------------------------------------------------------

/** 'preço do item 2 · modelo fiscal vigente para X + Y · …': os detalhes vêm prontos da API, sem repetição. */
export function listaPendenciasRascunho(pendencias: readonly PendenciaRascunhoFiscal[]): string {
  return [...new Set(pendencias.map((pendencia) => pendencia.detalhe))].join(' · ');
}

const ROTULOS_LINHA: Record<TipoLinhaRascunho, string> = { PECA: 'Peça', TINTA: 'Tinta', MAO_DE_OBRA: 'Mão de obra' };

export function rotuloLinhaRascunho(tipo: TipoLinhaRascunho): string {
  return ROTULOS_LINHA[tipo];
}

const ROTULOS_ESTADO_RASCUNHO: Record<EstadoRascunhoFiscal, string> = {
  PENDENTE: 'Pendente',
  PRONTO: 'Pronto para revisão',
  APROVADO: 'Aprovado',
};

export function rotuloEstadoRascunho(estado: EstadoRascunhoFiscal): string {
  return ROTULOS_ESTADO_RASCUNHO[estado];
}

/**
 * Valor do campo do rascunho para exibição/edição: dado faltante (null/undefined/vazio) aparece VAZIO — nunca um valor padrão
 * (D-17, T-12-79). O zero é um valor real e é preservado.
 */
export function valorOuVazio(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  if (typeof valor === 'number') return Number.isFinite(valor) ? paraTexto(valor) : '';
  return valor;
}

/**
 * Quantidade/valor do rascunho como texto de CAMPO editável em pt-BR. A API manda decimais como texto com ponto ("10.5000");
 * sem esta conversão o ponto seria lido como milhar/ambíguo pelo parser estrito. Texto não numérico volta como veio.
 */
export function numeroDoRascunhoParaCampo(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  if (typeof valor === 'string') {
    const texto = valor.trim();
    if (texto === '') return '';
    return /^-?\d+(\.\d+)?$/.test(texto) ? paraTexto(Number(texto)) : valor;
  }
  return valorOuVazio(valor);
}

/** Confirmação de "Aprovar rascunho" (WR-D1-04): o efeito fiscal/Omie é explicado antes do clique que grava. */
export const TITULO_APROVAR_RASCUNHO = 'Aprovar rascunho fiscal';
export const COPIA_APROVAR_RASCUNHO =
  'Aprovar libera o envio da nota fiscal e do título deste Retorno à Omie. Depois de aprovado, o rascunho não pode mais ser alterado ou recalculado por esta tela. Confira os valores, os dados fiscais e as quantidades antes de confirmar.';

/** 'Aprovar rascunho' só com o rascunho PRONTO (o servidor recusa PENDENTE com 409). */
export function podeAprovar({ estado }: { readonly estado: EstadoRascunhoFiscal }): boolean {
  return estado === 'PRONTO';
}

/** 'Ajustar valores' só com o rascunho PRONTO (D-18, A-32). */
export function podeAjustarRascunho({ estado }: { readonly estado: EstadoRascunhoFiscal }): boolean {
  return estado === 'PRONTO';
}

/** Escolher outra versão do modelo fiscal e 'Recalcular' valem em PENDENTE e PRONTO; depois de APROVADO o rascunho é imutável. */
export function podeSelecionarModeloFiscal({ estado }: { readonly estado: EstadoRascunhoFiscal }): boolean {
  return estado !== 'APROVADO';
}

/** Pendências que a seleção auditada de versão do modelo fiscal (mesmo cliente + material) pode resolver. */
export function pendenciaResolvivelPorModeloFiscal(codigo: CodigoPendenciaRascunho): boolean {
  return codigo === 'SEM_MODELO_FISCAL' || codigo === 'MODELO_FISCAL_INCOMPLETO';
}

// ---------------------------------------------------------------------------------------------------------------------
// Fase 13, Plano 32 (D-04, R13-12): de onde veio o consumo da tinta numa linha do rascunho fiscal.
// ---------------------------------------------------------------------------------------------------------------------

/** Classes do badge "Consumo estimado" que a revisão do rascunho (Fase 12) já usa: o layout da tela não muda. */
const CLASSE_BADGE_ESTIMADO = 'border-amber-300 bg-amber-50 text-amber-800';

export interface ApresentacaoFonteConsumo {
  readonly fonte: FonteConsumoResumida;
  readonly rotulo: string;
  readonly icones: readonly IconeFonteConsumo[];
  readonly classe: string;
  /** Explicação da origem; na fonte mista descreve as duas origens (pesagem do lote + rateio por área do dia). */
  readonly detalhe: string;
  /** `true` só quando o consumo caiu no insumo previsto do roteiro (badge amber "Consumo estimado"). */
  readonly estimado: boolean;
}

/**
 * Badge da fonte do consumo de uma linha de TINTA. `consumoFonte` vem da nova estratégia (Pesagem / Rateio por área /
 * Mista / Apontamento / Estimado). Rascunho antigo, sem o campo, só mostra algo quando `consumoEstimado` é verdadeiro
 * (o badge "Consumo estimado" da Fase 12); caso contrário devolve `null` (peça, mão de obra e tinta sem fonte).
 */
export function apresentacaoFonteConsumo(
  consumoFonte: FonteConsumoResumida | null | undefined,
  consumoEstimado: boolean | null | undefined,
): ApresentacaoFonteConsumo | null {
  const fonte: FonteConsumoResumida | null = consumoFonte ?? (consumoEstimado === true ? 'ESTIMADO' : null);
  if (fonte === null) return null;
  const base = apresentacaoFonte(fonte);
  const estimado = fonte === 'ESTIMADO';
  return {
    fonte,
    rotulo: base.rotulo,
    icones: base.icones,
    classe: estimado ? CLASSE_BADGE_ESTIMADO : base.classe,
    detalhe: base.detalhe,
    estimado,
  };
}
